import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import { SessionTransferStudent } from './sessionTransferApi';

const normalizeClassName = (name?: string) =>
  String(name || '')
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getClassRank = (name?: string): number | null => {
  const n = normalizeClassName(name);
  const direct: Record<string, number> = {
    NURSERY: 0,
    'LKG': 1,
    'L K G': 1,
    'UKG': 2,
    'U K G': 2,
    FIRST: 3,
    SECOND: 4,
    THIRD: 5,
    FOURTH: 6,
    FIFTH: 7,
    SIXTH: 8,
    SEVENTH: 9,
    EIGHTH: 10,
    NINTH: 11,
    TENTH: 12,
  };

  if (direct[n] !== undefined) {
    return direct[n];
  }

  if (n.includes('ELEVENTH')) return 13;
  if (n.includes('TWELFTH') || n.includes('TWELTH')) return 14;

  return null;
};

const sortBySequence = (list: any[]) => {
  return [...list].sort((a: any, b: any) => {
    const aRank = getClassRank(a.name);
    const bRank = getClassRank(b.name);

    if (aRank != null && bRank != null && aRank !== bRank) {
      return aRank - bRank;
    }

    if (aRank != null && bRank == null) return -1;
    if (aRank == null && bRank != null) return 1;

    const aOrder = Number(a.sequenceOrder ?? Number.MAX_VALUE);
    const bOrder = Number(b.sequenceOrder ?? Number.MAX_VALUE);
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return String(a.name || '').localeCompare(String(b.name || ''));
  });
};

const getAllowedClasses = (student: SessionTransferStudent, classes: any[]) => {
  if (!classes || classes.length === 0) {
    return [];
  }

  const currentClass = classes.find(
    (cls) =>
      cls.id === student.currentClass?.id ||
      String(cls.name).trim().toLowerCase() ===
        String(student.currentClass?.name).trim().toLowerCase()
  );

  if (!currentClass) {
    return [];
  }

  const currentRank = getClassRank(currentClass.name);
  if (currentRank != null) {
    const byRank = classes.filter((cls) => {
      const rank = getClassRank(cls.name);
      return rank != null && Math.abs(rank - currentRank) <= 1;
    });

    return sortBySequence(byRank.length > 0 ? byRank : [currentClass]);
  }

  const currentId = currentClass.id;
  const nextId = currentClass.nextClassId;
  const previousId = classes.find((cls) => cls.nextClassId === currentId)?.id;
  const byLink = classes.filter(
    (cls) => cls.id === currentId || cls.id === nextId || cls.id === previousId
  );

  return sortBySequence(byLink.length > 0 ? byLink : [currentClass]);
};

interface SessionTransferTableProps {
  students: SessionTransferStudent[];
  selectedStudents: string[];
  onToggleSelect: (studentId: string) => void;
  onToggleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  classes: any[];
  sections: Record<string, any[]>; // classId -> sections[]
  transferData: Record<string, { newClassId: string; newSectionId: string }>;
  onUpdateTransferData: (studentId: string, field: 'newClassId' | 'newSectionId', value: string) => void;
}

const SessionTransferTable: React.FC<SessionTransferTableProps> = ({
  students,
  selectedStudents,
  onToggleSelect,
  onToggleSelectAll,
  classes,
  sections,
  transferData,
  onUpdateTransferData
}) => {
  if (students.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No students found for the selected filters.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '60vh' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                checked={students.length > 0 && selectedStudents.length === students.length}
                onChange={onToggleSelectAll}
              />
            </TableCell>
            <TableCell>Admission No</TableCell>
            <TableCell>Scholar No</TableCell>
            <TableCell>Student Name</TableCell>
            <TableCell>Current Class</TableCell>
            <TableCell>Current Section</TableCell>
            <TableCell>New Class</TableCell>
            <TableCell>New Section</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {students.map((student) => {
            const isSelected = selectedStudents.indexOf(student.id) !== -1;
            const currentTransferData = transferData[student.id] || { newClassId: '', newSectionId: '' };
            const classSections = sections[currentTransferData.newClassId] || [];
            const allowedClasses = getAllowedClasses(student, classes);
            const currentSectionId = student.currentSection?.id;

            return (
              <TableRow key={student.id} selected={isSelected} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect(student.id)}
                  />
                </TableCell>
                <TableCell>{student.admissionNumber}</TableCell>
                <TableCell>{student.scholarNumber || '-'}</TableCell>
                <TableCell>{`${student.firstName} ${student.lastName}`}</TableCell>
                <TableCell>{student.currentClass.name}</TableCell>
                <TableCell>{student.currentSection.name}</TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={currentTransferData.newClassId}
                    onChange={(e) => onUpdateTransferData(student.id, 'newClassId', e.target.value)}
                  >
                    {allowedClasses.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    disabled={!currentTransferData.newClassId}
                    value={currentTransferData.newSectionId}
                    onChange={(e) => onUpdateTransferData(student.id, 'newSectionId', e.target.value)}
                  >
                    {classSections.map((sec) => (
                      <MenuItem key={sec.id} value={sec.id} disabled={currentTransferData.newClassId === student.currentClass.id && sec.id === currentSectionId}>
                        {sec.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SessionTransferTable;
